import { getCustomVariantDataAttributes } from '@unifyapps/carbon/no-code/utils/getCustomVariantDataAttributes';
import { getStringifiedValue } from '@unifyapps/carbon/no-code/utils/typography';
import { getConditionalIconNode } from '@unifyapps/carbon/utils/conditionalValues';
import { getInlineSvgIcon } from '@unifyapps/carbon/utils/getInlineSvgIcon';
import type { ButtonCustomStyles, ComputedButtonAppearance } from '@unifyapps/defs/types/blocks';
import { Button } from '@unifyapps/ui/components/Button';
import { clsx } from 'clsx';
import { forwardRef, type SyntheticEvent, useMemo } from 'react';
import type { LongPressHandlers } from 'use-long-press';

/** all known ButtonAppearance keys — anything else is a custom variant dimension */
const KNOWN_APPEARANCE_KEYS = new Set([
  'color',
  'size',
  'variant',
  'startDecorator',
  'endDecorator',
  'disabled',
  'loading',
  'styles',
  'className',
  'visibility',
  'customStyles',
]);

type BaseButtonProps = {
  appearance?: ComputedButtonAppearance;
  className?: string;
  loading?: boolean;
  onClick?: (e: SyntheticEvent) => void;
  onMouseEnter?: (e: SyntheticEvent) => void;
  size?: ComputedButtonAppearance['size'];
  style?: React.CSSProperties;
  type?: 'default' | 'submit' | 'reset';
  attributesAndEventListeners?: Record<string, unknown>;
  value: string;
  customStyle?: ButtonCustomStyles;
  longPressProps?: LongPressHandlers<HTMLButtonElement>;
  // native long-press handler; web uses longPressProps (use-long-press) instead
  onLongPress?: () => void;
  contentSlotNode?: React.ReactNode;
  contentMode?: 'text' | 'custom';
};

const BaseButton = forwardRef<HTMLButtonElement, BaseButtonProps>(function BaseButton(props, ref) {
  const {
    appearance,
    className,
    loading,
    onClick,
    onMouseEnter,
    size,
    style,
    customStyle,
    type,
    attributesAndEventListeners,
    value,
    longPressProps,
    onLongPress,
    contentSlotNode,
    contentMode,
    ...rest
  } = props;

  // custom variant dimensions (e.g. radius → data-radius="pill") applied as data attributes
  const customVariantDataAttrs = useMemo(
    () =>
      getCustomVariantDataAttributes(
        appearance as Record<string, unknown> | undefined,
        KNOWN_APPEARANCE_KEYS,
      ),
    [appearance],
  );

  const buttonEl = (
    <Button
      {...rest}
      aria-disabled={appearance?.disabled}
      className={clsx(className, appearance?.className)}
      color={appearance?.color ?? 'brand'}
      customStyle={customStyle}
      disabled={appearance?.disabled}
      endDecoratorComponent={getInlineSvgIcon(appearance?.endDecorator)}
      endDecoratorNode={getConditionalIconNode(appearance?.endDecorator, size)}
      loading={appearance?.loading || loading}
      onClick={onClick}
      onLongPress={onLongPress}
      onMouseEnter={onMouseEnter}
      ref={ref}
      size={size}
      startDecoratorComponent={getInlineSvgIcon(appearance?.startDecorator)}
      startDecoratorNode={getConditionalIconNode(appearance?.startDecorator, size)}
      style={style}
      type={type === 'default' ? 'button' : type}
      value={getStringifiedValue(value)}
      variant={appearance?.variant ?? 'solid'}
      {...customVariantDataAttrs}
      {...longPressProps}
      {...attributesAndEventListeners}
    >
      {contentMode === 'custom' ? contentSlotNode : `${getStringifiedValue(value)}`}
    </Button>
  );

  return buttonEl;
});

export default BaseButton;
